import { Tabs as T } from "radix-ui";
import { useEffect, useRef, type ReactNode } from "react";

export interface TabItem<V extends string> {
  value: V;
  label: ReactNode;
  /** Счётчик очереди рядом с подписью. */
  count?: number;
  /** Счётчик красным: в очереди есть просрочка. */
  alert?: boolean;
}

export interface TabsProps<V extends string> {
  value: V;
  onValueChange: (value: V) => void;
  items: TabItem<V>[];
  label: string;
  children?: ReactNode;
}

/**
 * Вкладки Д2: заглавными, выбранная с чёрной чертой снизу (docs/ui-rules.md,
 * «4»). На узком экране ряд прокручивается, а не переносится и не режет
 * подпись молча («2»).
 */
export function Tabs<V extends string>({ value, onValueChange, items, label, children }: TabsProps<V>) {
  const list = useRef<HTMLDivElement>(null);

  // Выбранная вкладка видна целиком, даже если ряд прокручен.
  useEffect(() => {
    list.current?.querySelector<HTMLElement>('[data-state="active"]')?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [value]);

  return (
    <T.Root value={value} onValueChange={(v) => onValueChange(v as V)} className="w-tabs" activationMode="manual">
      <T.List ref={list} className="w-tabs-list" aria-label={label}>
        {items.map((it) => (
          <T.Trigger key={it.value} value={it.value} className="w-tab">
            {it.label}
            {it.count !== undefined ? (
              <span className="w-tab-count w-num" data-alert={it.alert ? "" : undefined}>
                {it.count}
              </span>
            ) : null}
          </T.Trigger>
        ))}
      </T.List>
      {children}
    </T.Root>
  );
}

/** Содержимое вкладки: монтируется только выбранная. */
export const TabPanel = T.Content;
